import React,{useEffect,useRef,useState} from "react";
import {useLocation,useNavigate} from "react-router-dom";
import AuthService from "./AuthService";
import axiosInstance from "./axiosInstance";
import {getInternalBranding} from "./Dashboards/dashboardService";
import {getNotifications,markNotificationRead} from "./Communications/communicationService";

function getInitials(user){
 const source=user?.full_name||`${user?.first_name||""} ${user?.last_name||""}`.trim()||user?.email||user?.policy_number||"User";
 return source.split(" ").filter(Boolean).slice(0,2).map(part=>part[0]).join("").toUpperCase();
}

function formatLoginTime(value){
 if(!value)return "Current session";
 const date=new Date(value);
 if(Number.isNaN(date.getTime()))return "Current session";
 return date.toLocaleString([],{dateStyle:"medium",timeStyle:"short"});
}

function pageTitle(path){
 if(path.startsWith("/proposals")||path.startsWith("/agent/proposals"))return "Proposals";
 if(path.startsWith("/policies"))return "Policies";
 if(path.startsWith("/policy-maintenance"))return "Policy Maintenance";
 if(path.startsWith("/claims"))return "Claims";
 if(path.startsWith("/finance"))return "Finance & Transactions";
 if(path.startsWith("/commissions"))return "Commissions";
 if(path.startsWith("/insurance-reports"))return "Insurance Reports";
 if(path.startsWith("/audit-trail"))return "Audit Trail";
 if(path.startsWith("/assurance-ai"))return "NetriAssure Intelligence";
 if(path.startsWith("/settings"))return "System Settings";
 if(path.startsWith("/policyholder"))return "My Policy";
 if(path.startsWith("/agent"))return "Agent Dashboard";
 return "Dashboard";
}

export default function CustomNavbar({onToggleSidebar}){
 const navigate=useNavigate();
 const location=useLocation();
 const user=AuthService.getCurrentUser();
 const permissions=Array.isArray(user?.permissions)?user.permissions:[];
 const canSettings=
  user?.access_type==="SYSTEM_USER"&&(
   permissions.includes("*")||
   permissions.includes("manage_users")||
   permissions.includes("manage_roles")||
   permissions.includes("manage_general_settings")||
   permissions.includes("manage_branches")
  );
 const menuRef=useRef(null);
 const notificationRef=useRef(null);
 const [brand,setBrand]=useState(null);
 const [menuOpen,setMenuOpen]=useState(false);
 const [search,setSearch]=useState("");
 const [searchSuggestions,setSearchSuggestions]=useState([]);
 const [unreadCount,setUnreadCount]=useState(0);
 const [notificationOpen,setNotificationOpen]=useState(false);
 const [recentNotifications,setRecentNotifications]=useState([]);
 const role=typeof user?.role==="string"?user.role:user?.role?.name||user?.user_type||"User";
 const branch=user?.branch?.name||(user?.is_head_office_user?"Head Office":"Not assigned");

 useEffect(()=>{let active=true;getInternalBranding().then(result=>active&&setBrand(result)).catch(()=>{});return()=>{active=false}},[]);
 useEffect(()=>{
  let active=true;
  const load=()=>getNotifications().then(result=>{
   if(!active)return;
   setUnreadCount(result.unread_count||0);
   setRecentNotifications((result.notifications||[]).slice(0,5));
  }).catch(()=>{});
  load();
  const timer=setInterval(load,60000);
  return()=>{active=false;clearInterval(timer)}
 },[]);
 useEffect(()=>{
  let active=true;
  const value=search.trim();
  if(value.length<2||user?.access_type==="POLICY_HOLDER"){
   setSearchSuggestions([]);
   return()=>{active=false};
  }
  const timer=setTimeout(async()=>{
   try{
    const endpoint=user?.access_type==="AGENT"?"proposals/":"policies/";
    const response=await axiosInstance.get(endpoint,{
     params:{search:value,page_size:8}
    });
    if(!active)return;
    const data=response.data?.results||response.data||[];
    setSearchSuggestions(data.slice(0,8));
   }catch{
    if(active)setSearchSuggestions([]);
   }
  },180);
  return()=>{active=false;clearTimeout(timer)};
 },[search,user?.access_type]);

 useEffect(()=>{
  const close=event=>{
   if(menuRef.current&&!menuRef.current.contains(event.target))setMenuOpen(false);
   if(notificationRef.current&&!notificationRef.current.contains(event.target))setNotificationOpen(false);
  };
  document.addEventListener("mousedown",close);
  return()=>document.removeEventListener("mousedown",close)
 },[]);

 const submitSearch=event=>{
  event.preventDefault();
  const value=search.trim();
  if(!value)return;

  if(user?.access_type==="AGENT"){
   navigate(`/agent/proposals?search=${encodeURIComponent(value)}`);
  }else if(user?.access_type==="POLICY_HOLDER"){
   navigate("/policyholder/dashboard");
  }else{
   navigate(`/policies?search=${encodeURIComponent(value)}`);
  }
 };

 const logout=()=>{AuthService.logout();navigate("/")};

 return <nav className="netri-navbar">
  <button type="button" className="netri-icon-button netri-menu-button" onClick={onToggleSidebar} aria-label="Toggle sidebar"><i className="bi bi-list"/></button>

  <div className="netri-navbar-title">
   <strong>{pageTitle(location.pathname)}</strong>
   <span>{brand?.company_name||"NetriAssure"}</span>
  </div>

  <div className="netri-navbar-search-wrap">
   <form className="netri-navbar-search" onSubmit={submitSearch}>
    <i className="bi bi-search"/>
    <input
     value={search}
     onChange={event=>setSearch(event.target.value)}
     placeholder={user?.access_type==="AGENT"?"Search my proposals":"Search policy number or policyholder"}
     autoComplete="off"
    />
   </form>
   {searchSuggestions.length>0&&<div className="netri-search-suggestions">
    {searchSuggestions.map(item=><button
     type="button"
     key={`${user?.access_type}-${item.id}`}
     onClick={()=>{
      setSearch("");
      setSearchSuggestions([]);
      if(user?.access_type==="AGENT"){
       navigate(`/agent/proposals?search=${encodeURIComponent(item.code||"")}`);
      }else{
       navigate(`/policies?policy=${item.id}`);
      }
     }}
    >
     <strong>{item.code}</strong>
     <span>{item.policyholder_name||item.plan_name||item.status||""}</span>
     <small>{item.plan_name||item.status||""}</small>
    </button>)}
   </div>}
  </div>

  <div className="netri-notification-menu" ref={notificationRef}>
   <button type="button" className="netri-icon-button netri-notification-button" onClick={()=>setNotificationOpen(open=>!open)} aria-label="Notifications" title="Notifications">
    <i className="bi bi-bell"/>
    {unreadCount>0&&<span>{unreadCount>99?"99+":unreadCount}</span>}
   </button>

   {notificationOpen&&<div className="netri-notification-dropdown">
    <header><div><strong>Notifications</strong><span>{unreadCount} unread</span></div><button onClick={()=>{setNotificationOpen(false);navigate("/notifications")}}>View all</button></header>
    <div className="netri-notification-preview-list">
     {recentNotifications.length?recentNotifications.map(notification=>
      <button key={notification.id} className={notification.is_read?"":"unread"} onClick={async()=>{
       // Never allow a read-status request to destroy navigation or session.
       if(!notification.is_read){
        try{
         await markNotificationRead(notification.id);
         setUnreadCount(count=>Math.max(0,count-1));
         setRecentNotifications(rows=>rows.map(row=>row.id===notification.id?{...row,is_read:true}:row));
        }catch(error){
         console.error("Could not mark notification as read.",error);
        }
       }
       setNotificationOpen(false);
       if(notification.action_url?.startsWith("/notifications?handover=")||notification.action_url?.startsWith("/notifications?service_request=")){
        navigate(notification.action_url);
       }else{
        navigate("/notifications",{state:{notificationId:notification.id}});
       }
      }}>
       <i className="bi bi-bell"/>
       <div><strong>{notification.title}</strong><p>{notification.message}</p><span>{new Date(notification.created_at).toLocaleString()}</span></div>
       {!notification.is_read&&<em/>}
      </button>
     ):<div className="netri-notification-preview-empty">No notifications.</div>}
    </div>
    <footer><button onClick={()=>{setNotificationOpen(false);navigate("/notifications")}}>Open notification inbox</button></footer>
   </div>}
  </div>

  {canSettings&&<button
   type="button"
   className="netri-icon-button netri-settings-button"
   onClick={()=>navigate("/settings")}
   aria-label="System settings"
   title="System settings"
  >
   <i className="bi bi-gear"/>
  </button>}

  <div className="netri-profile-menu" ref={menuRef}>
   <button type="button" className="netri-profile-trigger" onClick={()=>setMenuOpen(open=>!open)} aria-expanded={menuOpen} aria-label="Account menu">
    <div className="netri-profile-avatar">
     {user?.profile_picture
      ?<img src={user.profile_picture} alt={user.full_name||"Profile"}/>
      :<span>{getInitials(user)}</span>}
    </div>
   </button>

   {menuOpen&&<div className="netri-profile-dropdown netri-profile-dropdown--simple">
    <div className="netri-profile-dropdown__head">
     <div className="netri-profile-avatar large">
      {user?.profile_picture
       ?<img src={user.profile_picture} alt={user.full_name||"Profile"}/>
       :<span>{getInitials(user)}</span>}
     </div>
     <div><strong>{user?.full_name||user?.email||user?.policy_number}</strong><span>{user?.email||user?.policy_number}</span></div>
    </div>
    <div className="netri-session-summary">
     <div><span>Role</span><strong>{role}</strong></div>
     <div><span>Branch</span><strong>{branch}</strong></div>
     <div className="wide"><span>Signed in</span><strong>{formatLoginTime(localStorage.getItem("login_time"))}</strong></div>
    </div>
    <div className="netri-profile-dropdown__links">
     <button onClick={()=>{setMenuOpen(false);navigate("/profile")}}><i className="bi bi-person-circle"/><span>Profile</span></button>
     <button onClick={()=>{setMenuOpen(false);navigate("/change-password")}}><i className="bi bi-key"/><span>Change password</span></button>
     <button className="logout" onClick={logout}><i className="bi bi-box-arrow-right"/><span>Sign out</span></button>
    </div>
   </div>}
  </div>
 </nav>
}
